import { useTranslation } from 'react-i18next';
import type { Service, Barber } from '@/types';
import { Clock, Sparkles, Scissors, User, Loader2, Zap } from 'lucide-react';
import { formatDuration } from '@/lib/timeUtils';
import { formatPrice } from '@/lib/formatUtils';

interface BookingSummaryProps {
  service: Service;
  barber: Barber;
  isAnyBarber: boolean;
  preAssignedBarber: Barber | null;
  loadingPreAssignment: boolean;
}

export function BookingSummary({
  service,
  barber,
  isAnyBarber,
  preAssignedBarber,
  loadingPreAssignment,
}: BookingSummaryProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
      {/* Service Summary */}
      <div className="bg-surface border border-hairline rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Scissors className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
          <h3 className="text-xs font-medium text-ink-subtle uppercase tracking-wide">
            {t('booking.confirmation.serviceSelected')}
          </h3>
        </div>
        <div className="space-y-3">
          <p className="font-sans text-xl font-semibold text-ink">
            {service.name}
          </p>
          {service.description && (
            <p className="text-sm text-ink-muted">
              {service.description}
            </p>
          )}
          <div className="flex items-center justify-between pt-3 border-t border-hairline">
            <div className="flex items-center gap-2 text-ink-muted">
              <Clock className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
              <span className="text-sm font-medium">{formatDuration(service.duration, { short: true })}</span>
            </div>
            <div className="text-2xl font-semibold text-ink tabular-nums">
              {formatPrice(service.price)}
            </div>
          </div>
        </div>
      </div>

      {/* Barber Summary */}
      <div className="bg-surface border border-hairline rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          {isAnyBarber ? (
            <>
              <Sparkles className={`w-4 h-4 text-ink-subtle ${loadingPreAssignment ? 'animate-spin' : ''}`} aria-hidden="true" />
              <h3 className="text-xs font-medium text-ink-subtle uppercase tracking-wide">
                {loadingPreAssignment ? t('booking.confirmation.calculatingAssignment') : t('booking.confirmation.autoAssigned')}
              </h3>
            </>
          ) : (
            <>
              <User className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
              <h3 className="text-xs font-medium text-ink-subtle uppercase tracking-wide">
                {t('booking.confirmation.barberSelected')}
              </h3>
            </>
          )}
        </div>

        {loadingPreAssignment ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="w-7 h-7 text-ink-subtle animate-spin mx-auto mb-3" aria-hidden="true" />
              <p className="text-sm text-ink-muted">{t('booking.confirmation.calculatingBarber')}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            {isAnyBarber && preAssignedBarber ? (
              // Show pre-assigned barber
              <>
                {preAssignedBarber.photoURL ? (
                  <img
                    src={preAssignedBarber.photoURL}
                    alt={preAssignedBarber.displayName}
                    className="w-16 h-16 rounded-full object-cover ring-1 ring-accent"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center text-accent text-2xl font-semibold">
                    {preAssignedBarber.displayName[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-sans text-xl font-semibold text-ink mb-1">
                    {preAssignedBarber.displayName}
                  </p>
                  <p className="inline-flex items-center gap-1.5 text-xs font-medium text-accent mb-2">
                    <Zap className="w-3.5 h-3.5" aria-hidden="true" /> {t('booking.confirmation.shortestQueue')}
                  </p>
                  {preAssignedBarber.specialties && preAssignedBarber.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {preAssignedBarber.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full text-xs font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Show selected barber normally
              <>
                {barber.photoURL ? (
                  <img
                    src={barber.photoURL}
                    alt={barber.displayName}
                    className="w-16 h-16 rounded-full object-cover ring-1 ring-hairline"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-surface-3 flex items-center justify-center text-ink-muted text-2xl font-semibold">
                    {barber.displayName[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-sans text-xl font-semibold text-ink mb-1">
                    {barber.displayName}
                  </p>
                  {barber.specialties && barber.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {barber.specialties.slice(0, 3).map((specialty, idx) => (
                        <span
                          key={idx}
                          className="bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full text-xs font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
